'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const SWAP_INTERVAL = 5200;
const FALLBACK_1 = '/hero-photo-1.svg';
const FALLBACK_2 = '/hero-photo-2.svg';

export function HeroVisual({
  image1,
  image2,
}: {
  image1: string | null;
  image2: string | null;
}) {
  const a = image1 ?? FALLBACK_1;
  const b = image2 ?? FALLBACK_2;
  const [swapped, setSwapped] = useState(false);
  const [pulse, setPulse] = useState(0);
  const pausedRef = useRef(false);

  const toggle = useCallback(() => {
    setSwapped((s) => !s);
    setPulse((p) => p + 1);
  }, []);

  useEffect(() => {
    if (pausedRef.current) return;
    const id = window.setInterval(() => {
      if (!pausedRef.current) toggle();
    }, SWAP_INTERVAL);
    return () => window.clearInterval(id);
  }, [toggle]);

  useEffect(() => {
    const onVisibility = () => {
      pausedRef.current = document.hidden;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const front = swapped ? b : a;
  const back = swapped ? a : b;

  return (
    <div
      className="relative mx-auto w-full max-w-[340px] md:mx-0 md:w-[640px] md:max-w-none md:translate-x-[80px]"
      style={{ aspectRatio: '593 / 687' }}
    >
      {/* back slot (upper-right, green glow) */}
      <div
        className="hero-slot"
        style={{
          left: '10.07%',
          top: '4.04%',
          width: '55.13%',
          height: '61.76%',
          borderRadius: '45px 9px 45px 45px',
          boxShadow: '0 40px 80px -40px rgba(157, 217, 74, 0.55)',
        }}
      >
        <HeroPhoto src={back} pulseKey={`b-${pulse}`} direction="prev" />
      </div>

      {/* front slot (upper-left, orange glow) — clickable */}
      <button
        type="button"
        onClick={toggle}
        aria-label="Сменить фото"
        className="hero-slot hero-slot-button group"
        style={{
          left: 0,
          top: 0,
          width: '60.71%',
          height: '68.12%',
          borderRadius: '50px 10px 50px 50px',
          boxShadow: '0 40px 80px -40px rgba(239, 100, 68, 0.6)',
        }}
      >
        <HeroPhoto src={front} pulseKey={`f-${pulse}`} direction="next" />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] bg-gradient-to-tr from-white/0 via-white/0 to-white/15 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100"
          style={{ transform: 'translateY(6px)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 12h16m0 0l-6-6m6 6l-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/hero-ornament.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          left: '46%',
          top: '60%',
          width: '26.87%',
          height: '20.99%',
        }}
      />
    </div>
  );
}

function HeroPhoto({
  src,
  pulseKey,
  direction,
}: {
  src: string;
  pulseKey: string;
  direction: 'next' | 'prev';
}) {
  return (
    <div
      key={pulseKey}
      data-dir={direction}
      className="hero-photo absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${src})` }}
    />
  );
}
